import { API_BASE, api } from "@/lib/apiClient";

// ---------------------------------------------------------------------------
// Typen
// ---------------------------------------------------------------------------

export type MahnlaufFilter = "mahnbar" | "ueberfaellig" | "alle" | "gesperrt";

/** Status-Codes exakt wie im Backend (`MAHNUNG_STATUS`). */
export type MahnungStatus = "entwurf" | "erstellt" | "versendet" | "fehlgeschlagen" | "storniert";

export const MAHNUNG_STATUS_LABELS: Record<MahnungStatus, string> = {
  entwurf: "Entwurf",
  erstellt: "Finalisiert – noch nicht versendet",
  versendet: "Versendet",
  fehlgeschlagen: "Versand fehlgeschlagen",
  storniert: "Storniert",
};

/** Kurzlabel für Badges/Tabellen. */
export const MAHNUNG_STATUS_KURZ: Record<MahnungStatus, string> = {
  entwurf: "Entwurf",
  erstellt: "Finalisiert",
  versendet: "Versendet",
  fehlgeschlagen: "Fehlgeschlagen",
  storniert: "Storniert",
};

export function mahnungStatusLabel(status: string | null | undefined, kurz = false): string {
  const s = String(status || "") as MahnungStatus;
  return (kurz ? MAHNUNG_STATUS_KURZ : MAHNUNG_STATUS_LABELS)[s] || String(status || "—");
}

/** Versandarten exakt wie im Backend (`VERSANDARTEN`). */
export const VERSANDART_OPTIONS: Array<{ value: string; label: string; hinweis?: string }> = [
  { value: "email", label: "E-Mail", hinweis: "Versand direkt aus der App mit PDF-Anhang." },
  { value: "brief", label: "Brief (Post)", hinweis: "PDF ausdrucken und per Post versenden." },
  { value: "druck", label: "Ausdruck / Übergabe", hinweis: "Ausgedruckt, z. B. persönlich mitgegeben." },
  { value: "persoenlich", label: "Persönlich", hinweis: "Persönlich übergeben oder besprochen." },
  { value: "sonstige", label: "Sonstige", hinweis: "Anderer Weg – bitte in der Notiz beschreiben." },
];

export function versandartLabel(value: string | null | undefined): string {
  return VERSANDART_OPTIONS.find((o) => o.value === value)?.label || String(value || "—");
}

/** Herkunft der Empfänger-E-Mail (Backend `EMPFAENGER_QUELLEN`). */
export type EmpfaengerQuelle =
  | "stammdaten_kanal"
  | "stammdaten_rechnung"
  | "kunde"
  | "ansprechpartner"
  | "manuell"
  | "keine";

export interface MahnwesenPermissions {
  ansehen: boolean;
  erstellen: boolean;
  bearbeiten: boolean;
  finalisieren: boolean;
  versenden: boolean;
  stornieren: boolean;
  zins_ueberschreiben: boolean;
  verzugsbeginn_ueberschreiben: boolean;
  kosten_aendern: boolean;
  sperren_verwalten: boolean;
  vereinbarungen_verwalten: boolean;
  einstellungen: boolean;
}

export interface Mahnstufe {
  id: number;
  code: string;
  bezeichnung: string;
  interne_bezeichnung: string | null;
  sortierung: number;
  aktiv: boolean;
  tage_nach_faelligkeit: number;
  tage_nach_vorheriger: number;
  zahlungsfrist_tage: number;
  betreff: string;
  dokument_titel: string;
  anrede: string;
  einleitung: string;
  haupttext: string;
  zahlungsaufforderung: string;
  schlusstext: string;
  hinweistext: string;
  fusstext: string;
  verzugszinsen_aktiv: boolean;
  mahnkosten_aktiv: boolean;
  mahnkosten_betrag: number;
  verzugspauschale_aktiv: boolean;
  briefpapier_erste_id: number | null;
  briefpapier_folge_id: number | null;
  standard_versandart: string;
  email_betreff: string;
  email_text: string;
  /** Hervorgehobenen „Zahlbar bis“-Kasten unter der Zahlungsaufforderung drucken. */
  zahlungsfrist_box_anzeigen: boolean;
}

/** Mitgelieferte Standardtexte einer Stufe (Dokument + E-Mail). */
export type MahnstufeStandardtexte = Pick<
  Mahnstufe,
  | "betreff"
  | "dokument_titel"
  | "anrede"
  | "einleitung"
  | "haupttext"
  | "zahlungsaufforderung"
  | "schlusstext"
  | "hinweistext"
  | "fusstext"
  | "email_betreff"
  | "email_text"
>;

export interface MahnwesenEinstellungen {
  aktiv: boolean;
  standard_versandart: string;
  verbraucherhinweis_in_rechnung: boolean;
  verzugszinsen: {
    b2c: { verwenden: boolean; aufschlag: number };
    b2b: { verwenden: boolean; aufschlag: number; verzugspauschale: boolean };
  };
  verzugspauschale_betrag: number;
  rechnung_pdf_anhaengen: boolean;
}

export interface BasiszinsSatz {
  id: number;
  gueltig_ab: string;
  zinssatz_prozent: number;
  quelle: string | null;
  notiz: string | null;
}

export interface BasiszinsStand {
  aktueller_satz: number | null;
  gueltig_ab: string | null;
  gepflegt_bis: string | null;
  aktuell_gueltig: boolean;
}

export interface MahnlaufItem {
  dokument_id: number;
  dokument_nummer: string | null;
  kunde_name: string | null;
  kunden_id: number | null;
  kundennummer: string | null;
  kundenart: string | null;
  hero_document_id: number | null;
  belegdatum: string | null;
  faelligkeitsdatum: string | null;
  tage_ueberfaellig: number;
  summe_brutto: number;
  summe_bezahlt: number;
  summe_offen: number;
  aktuelle_stufe_code: string;
  aktuelle_stufe_label: string;
  letzte_mahnung_am: string | null;
  naechste_stufe_code: string | null;
  naechste_stufe_label: string | null;
  empfohlen_am: string | null;
  mahnbar: boolean;
  sperre_aktiv: boolean;
  sperre_status: string | null;
  pausiert: boolean;
  vereinbarung_aktiv: boolean;
  entwurf_ids: number[];
}

export interface MahnlaufKpis {
  offen_gesamt: number;
  ueberfaellig_gesamt: number;
  anzahl_offen: number;
  anzahl_ueberfaellig: number;
  heute_mahnbar: number;
  mit_sperre: number;
  mit_vereinbarung: number;
  durchschnitt_ueberfaellig_tage: number;
  offene_entwuerfe: number;
  nach_stufe: Record<string, number>;
}

export interface MahnlaufDaten {
  items: MahnlaufItem[];
  kpis: MahnlaufKpis;
  stufen: Mahnstufe[];
  einstellungen: MahnwesenEinstellungen;
  basiszins: BasiszinsStand;
  permissions: MahnwesenPermissions;
}

export interface MahnungListItem {
  id: number;
  nummer: string | null;
  status: MahnungStatus;
  stufe_code: string;
  stufe_bezeichnung: string;
  kunden_id: number | null;
  kunde_name: string | null;
  kundennummer: string | null;
  belegdatum: string | null;
  summe_hauptforderung: number;
  summe_zinsen: number;
  summe_kosten: number;
  summe_gesamt: number;
  versandart: string | null;
  versendet_am: string | null;
  versendet_via: string | null;
  legacy: boolean;
  zahlungsfrist_datum: string | null;
  created_by_name: string | null;
  created_at: string | null;
  hat_pdf: boolean;
  anzahl_rechnungen: number;
  rechnungsnummern: string | null;
}

export interface MahnungPosition {
  id: number;
  dokument_id: number;
  rechnung_nummer: string | null;
  rechnung_datum: string | null;
  faelligkeitsdatum: string | null;
  betrag_brutto: number;
  betrag_bezahlt: number;
  betrag_offen: number;
  zinsen: number;
  verzugsbeginn: string | null;
  verzugsbeginn_ermittelt: string | null;
  verzugsbeginn_grund: string | null;
  verzugsbeginn_grund_label: string | null;
  verzugsbeginn_manuell_von: string | null;
  sortierung: number;
}

export interface MahnungKosten {
  id: number;
  typ: string;
  bezeichnung: string;
  betrag: number;
  begruendung: string | null;
  rechtsgrundlage: string | null;
  automatisch: boolean;
  interne_notiz: string | null;
  sortierung: number;
}

export interface ZinsPeriodeAnzeige {
  rechnung_nummer: string | null;
  verzugsbeginn: string | null;
  verzugsbeginn_grund: string | null;
  von: string | null;
  bis: string | null;
  tage: number;
  jahr_tage: number;
  grundlage_betrag: number;
  basiszinssatz: number | null;
  aufschlag: number | null;
  zinssatz: number;
  zinsbetrag: number;
}

export interface MahnungWarnung {
  typ: "fehler" | "warnung" | "hinweis" | string;
  text: string;
}

export interface MahnungDetail {
  id: number;
  nummer: string | null;
  status: MahnungStatus;
  stufe_id: number | null;
  stufe_code: string;
  stufe_bezeichnung: string;
  kunden_id: number | null;
  /** Geschäftspartner (Kundenstammdaten) – für den Link „Stammdaten öffnen“. */
  geschaeftspartner_id: number | null;
  kunde_name: string | null;
  kunde_adresszusatz: string | null;
  kunde_strasse: string | null;
  kunde_hausnummer: string | null;
  kunde_plz: string | null;
  kunde_ort: string | null;
  kunde_email: string | null;
  kundennummer: string | null;
  kundenart: string | null;
  ansprechpartner_name: string | null;
  ansprechpartner_email?: string | null;
  ansprechpartner_telefon?: string | null;
  empfaenger: string | null;
  empfaenger_cc: string | null;
  empfaenger_quelle: EmpfaengerQuelle | string | null;
  empfaenger_quelle_label: string | null;
  /** Team-Adressen, die beim Versand zusätzlich in CC gehen. */
  team_cc?: string | null;
  versandart: string | null;
  belegdatum: string | null;
  zahlungsfrist_tage: number | null;
  zahlungsfrist_datum: string | null;
  zahlungsfrist_box_anzeigen: boolean;
  betreff: string;
  dokument_titel: string;
  anrede: string;
  einleitung: string;
  haupttext: string;
  zahlungsaufforderung: string;
  schlusstext: string;
  hinweistext: string;
  fusstext: string;
  email_betreff: string;
  email_text: string;
  interne_notiz: string | null;
  verzugszinsen_aktiv: boolean;
  zins_modus: string;
  zins_zinssatz: number | null;
  zins_aufschlag: number | null;
  zins_festbetrag: number | null;
  zins_stichtag: string | null;
  verzugspauschale_aktiv: boolean;
  summe_hauptforderung: number;
  summe_zinsen: number;
  summe_kosten: number;
  summe_gesamt: number;
  versendet_am: string | null;
  versendet_via: string | null;
  storniert_am: string | null;
  storno_grund: string | null;
  legacy: boolean;
  hat_pdf: boolean;
  pdf_filename: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_by_email: string | null;
  created_by_telefon: string | null;
  created_by_mobil: string | null;
  created_at: string | null;
  positionen: MahnungPosition[];
  kosten: MahnungKosten[];
  zinsperioden: unknown[];
  warnungen: MahnungWarnung[];
  /** Verständliche Hinweise zu doppelten Aussagen in den Texten (Anrede, Frist …). */
  text_hinweise: string[];
  zinsberechnung: ZinsPeriodeAnzeige[];
  permissions: MahnwesenPermissions;
  basiszins: BasiszinsStand;
}

export interface MahnungPatch {
  betreff?: string;
  dokument_titel?: string;
  anrede?: string;
  einleitung?: string;
  haupttext?: string;
  zahlungsaufforderung?: string;
  schlusstext?: string;
  hinweistext?: string;
  fusstext?: string;
  email_betreff?: string;
  email_text?: string;
  interne_notiz?: string;
  kunde_name?: string;
  kunde_adresszusatz?: string;
  kunde_strasse?: string;
  kunde_hausnummer?: string;
  kunde_plz?: string;
  kunde_ort?: string;
  kunde_email?: string;
  ansprechpartner_name?: string;
  empfaenger?: string;
  empfaenger_cc?: string;
  created_by_name?: string;
  created_by_email?: string;
  created_by_telefon?: string;
  created_by_mobil?: string;
  versandart?: string;
  zahlungsfrist_datum?: string;
  zahlungsfrist_tage?: number;
  zahlungsfrist_box_anzeigen?: boolean;
  verzugszinsen_aktiv?: boolean;
  zins_modus?: string;
  zins_zinssatz?: number;
  zins_aufschlag?: number;
  zins_festbetrag?: number;
  zins_stichtag?: string;
  verzugspauschale_aktiv?: boolean;
  positionen?: Array<{ id: number; verzugsbeginn?: string | null }>;
  kosten?: Array<{
    position_id?: number;
    typ: string;
    bezeichnung: string;
    betrag: number;
    begruendung?: string;
    rechtsgrundlage?: string;
    automatisch?: boolean;
    interne_notiz?: string;
  }>;
}

export interface Mahnsperre {
  id: number;
  dokument_id: number | null;
  kunden_id: number | null;
  status: string;
  grund: string | null;
  gueltig_bis: string | null;
  aktenzeichen: string | null;
  aktiv: boolean;
  created_by_name: string | null;
  created_at: string | null;
  beendet_am: string | null;
}

export interface Zahlungsvereinbarung {
  id: number;
  dokument_id: number | null;
  kunden_id: number | null;
  titel: string | null;
  vereinbarter_betrag: number | null;
  naechste_rate_am: string | null;
  naechste_rate_betrag: number | null;
  mahnsperre: boolean;
  status: string;
  notiz: string | null;
}

export interface MahnungEvent {
  id: number;
  mahnung_id: number | null;
  dokument_id: number | null;
  typ: string;
  nachricht: string | null;
  daten: unknown;
  user_login: string | null;
  user_name: string | null;
  created_at: string | null;
}

export interface DokumentMahnungStatus {
  dokument_id: number;
  dokument_nummer: string | null;
  status: string;
  faelligkeitsdatum: string | null;
  tage_ueberfaellig: number;
  summe_brutto: number;
  summe_bezahlt: number;
  summe_offen: number;
  mahnstufe: string;
  mahnstufe_label: string;
  naechste_stufe: string | null;
  naechste_stufe_label: string | null;
  empfohlen_am: string | null;
  mahnung_faellig: boolean;
  mahnung_pausiert: boolean;
  mahnsperre_aktiv: boolean;
  max_stufe_erreicht: boolean;
  letzte_mahnung_am: string | null;
  verzug: unknown;
  historie: MahnungListItem[];
  sperren: Mahnsperre[];
  vereinbarungen: Zahlungsvereinbarung[];
  permissions: MahnwesenPermissions;
}

export interface KundenKonto {
  offene_posten: Array<{
    id: number;
    dokument_nummer: string | null;
    belegdatum: string | null;
    faelligkeitsdatum: string | null;
    status: string;
    mahnstufe: string;
    letzte_mahnung_am: string | null;
    mahnung_pausiert: boolean;
    summe_brutto: number;
    summe_bezahlt: number;
    summe_offen: number;
    tage_ueberfaellig: number;
  }>;
  zahlungen: Array<{
    id: number;
    dokument_id: number;
    dokument_nummer: string | null;
    betrag: number;
    bezahlt_am: string | null;
    referenz: string | null;
    notiz: string | null;
  }>;
  mahnungen: MahnungListItem[];
  sperren: Mahnsperre[];
  vereinbarungen: Zahlungsvereinbarung[];
}

// ---------------------------------------------------------------------------
// Mahnlauf / Übersicht
// ---------------------------------------------------------------------------

export async function loadMahnlauf(params?: {
  filter?: MahnlaufFilter;
  q?: string;
  stufe?: string;
  kundenart?: string;
  min_betrag?: number;
}): Promise<MahnlaufDaten> {
  const qs = new URLSearchParams();
  if (params?.filter) qs.set("filter", params.filter);
  if (params?.q) qs.set("q", params.q);
  if (params?.stufe) qs.set("stufe", params.stufe);
  if (params?.kundenart) qs.set("kundenart", params.kundenart);
  if (params?.min_betrag != null) qs.set("min_betrag", String(params.min_betrag));
  const suffix = qs.toString() ? `?${qs}` : "";
  return api.get<MahnlaufDaten>(`/mahnwesen${suffix}`);
}

/** Anzahl Rechnungen, für die aktuell eine Mahnung fällig ist (Nav-Badge). */
export async function fetchMahnwesenFaelligCount(): Promise<number> {
  const data = await api.get<{ count?: number }>("/mahnwesen/faellig-count");
  return Number(data?.count || 0);
}

export async function erstelleMahnungsEntwuerfe(payload: {
  dokument_ids: number[];
  stufe_code?: string;
  sammeln?: boolean;
}): Promise<{
  entwuerfe: Array<{
    id: number;
    stufe_bezeichnung: string;
    kunde_name: string | null;
    anzahl_rechnungen: number;
    summe_gesamt: number;
  }>;
}> {
  return api.post("/mahnwesen/entwuerfe", {
    dokument_ids: payload.dokument_ids,
    stufe_code: payload.stufe_code,
    sammeln: payload.sammeln ?? true,
  });
}

// ---------------------------------------------------------------------------
// Mahnungen (Vorgänge)
// ---------------------------------------------------------------------------

export async function listMahnungen(params?: {
  q?: string;
  status?: string;
  stufe?: string;
  kunden_id?: number;
  dokument_id?: number;
  auftrag_id?: number;
  von?: string;
  bis?: string;
  limit?: number;
}): Promise<MahnungListItem[]> {
  const qs = new URLSearchParams();
  if (params?.q) qs.set("q", params.q);
  if (params?.status) qs.set("status", params.status);
  if (params?.stufe) qs.set("stufe", params.stufe);
  if (params?.kunden_id != null) qs.set("kunden_id", String(params.kunden_id));
  if (params?.dokument_id != null) qs.set("dokument_id", String(params.dokument_id));
  if (params?.auftrag_id != null) qs.set("auftrag_id", String(params.auftrag_id));
  if (params?.von) qs.set("von", params.von);
  if (params?.bis) qs.set("bis", params.bis);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  const data = await api.get<{ items: MahnungListItem[] }>(`/mahnwesen/mahnungen${suffix}`);
  return data.items || [];
}

export async function getMahnung(id: number): Promise<MahnungDetail> {
  return api.get<MahnungDetail>(`/mahnwesen/mahnungen/${id}`);
}

export async function updateMahnung(id: number, patch: MahnungPatch): Promise<MahnungDetail> {
  return api.put<MahnungDetail>(`/mahnwesen/mahnungen/${id}`, patch);
}

export async function finalisiereMahnung(id: number, force = false): Promise<MahnungDetail> {
  return api.post<MahnungDetail>(`/mahnwesen/mahnungen/${id}/finalisieren`, { force });
}

export async function sendeMahnung(
  id: number,
  payload: {
    to: string;
    cc?: string;
    bcc?: string;
    subject: string;
    text_body?: string;
    html_body?: string;
    rechnungen_anhaengen?: boolean;
    /** Gezielte Auswahl der anzuhängenden Rechnungen (Dokument-IDs). */
    anhang_dokument_ids?: number[];
  },
): Promise<{ ok: boolean; via?: string }> {
  return api.post(`/mahnwesen/mahnungen/${id}/senden`, {
    to: payload.to,
    cc: payload.cc || "",
    bcc: payload.bcc || "",
    subject: payload.subject,
    text_body: payload.text_body || "",
    html_body: payload.html_body || "",
    rechnungen_anhaengen: Boolean(payload.rechnungen_anhaengen),
    anhang_dokument_ids: payload.anhang_dokument_ids ?? null,
  });
}

// --- Entwurf: Rechnungen anfügen/entfernen, Empfänger & Texte neu laden -------------

export interface OffeneRechnungFuerMahnung {
  dokument_id: number;
  dokument_nummer: string | null;
  belegdatum: string | null;
  faelligkeitsdatum: string | null;
  summe_brutto: number;
  summe_bezahlt: number;
  summe_offen: number;
  tage_ueberfaellig: number;
  mahnstufe: string | null;
  pausiert: boolean;
}

/** Weitere offene Rechnungen desselben Kunden, die dem Entwurf hinzugefügt werden können. */
export async function listOffeneRechnungenFuerMahnung(id: number): Promise<OffeneRechnungFuerMahnung[]> {
  const data = await api.get<{ items: OffeneRechnungFuerMahnung[] }>(
    `/mahnwesen/mahnungen/${id}/offene-rechnungen`,
  );
  return data.items || [];
}

export async function fuegeRechnungenHinzu(id: number, dokumentIds: number[]): Promise<MahnungDetail> {
  return api.post<MahnungDetail>(`/mahnwesen/mahnungen/${id}/positionen`, { dokument_ids: dokumentIds });
}

export async function entferneRechnung(id: number, positionId: number): Promise<MahnungDetail> {
  return api.del<MahnungDetail>(`/mahnwesen/mahnungen/${id}/positionen/${positionId}`);
}

/** Empfänger-E-Mail erneut aus den Kundenstammdaten übernehmen. */
export async function ladeEmpfaengerNeu(id: number): Promise<MahnungDetail> {
  return api.post<MahnungDetail>(`/mahnwesen/mahnungen/${id}/empfaenger-neu-laden`, {});
}

/** Dokument- und E-Mail-Texte aus der Mahnstufe neu übernehmen (verwirft eigene Änderungen). */
export async function setzeTexteZurueck(id: number): Promise<MahnungDetail> {
  return api.post<MahnungDetail>(`/mahnwesen/mahnungen/${id}/texte-zuruecksetzen`, {});
}

export async function dokumentiereVersand(
  id: number,
  payload: { versandart: string; notiz?: string },
): Promise<{ ok: boolean }> {
  return api.post(`/mahnwesen/mahnungen/${id}/versand-dokumentieren`, {
    versandart: payload.versandart,
    notiz: payload.notiz || "",
  });
}

export async function storniereMahnung(id: number, grund: string): Promise<{ ok: boolean }> {
  return api.post(`/mahnwesen/mahnungen/${id}/stornieren`, { grund });
}

export async function deleteMahnungEntwurf(id: number): Promise<{ ok: boolean }> {
  return api.del(`/mahnwesen/mahnungen/${id}`);
}

export async function listMahnungEvents(id: number): Promise<MahnungEvent[]> {
  const data = await api.get<{ items: MahnungEvent[] }>(`/mahnwesen/mahnungen/${id}/events`);
  return data.items || [];
}

export function mahnungVorschauUrl(id: number, seite: "erste" | "folge" = "erste"): string {
  const path = `/mahnwesen/mahnungen/${id}/vorschau?seite=${seite}&t=${Date.now()}`;
  return API_BASE ? `${API_BASE}${path}` : path;
}

function messageFromMahnungResponse(text: string, status: number, statusText: string): string {
  const trimmed = text.trim();
  if (trimmed.startsWith("{")) {
    try {
      const body = JSON.parse(trimmed) as { detail?: unknown };
      if (typeof body.detail === "string" && body.detail.trim()) return body.detail.trim();
    } catch {
      /* Rohtext verwenden */
    }
  }
  return trimmed || `${status} ${statusText}`;
}

/** HTML-Vorschau per Session laden (iframe darf die API-URL nicht direkt einbetten). */
export async function fetchMahnungVorschauHtml(
  id: number,
  seite: "erste" | "folge" = "erste",
  signal?: AbortSignal,
): Promise<string> {
  const res = await fetch(mahnungVorschauUrl(id, seite), {
    credentials: "include",
    signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(messageFromMahnungResponse(text, res.status, res.statusText));
  }
  return res.text();
}

export function mahnungPdfUrl(id: number): string {
  const path = `/mahnwesen/mahnungen/${id}/pdf`;
  return API_BASE ? `${API_BASE}${path}` : path;
}

export async function fetchMahnungPdf(id: number, signal?: AbortSignal): Promise<Blob> {
  const res = await fetch(mahnungPdfUrl(id), { credentials: "include", signal });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

// ---------------------------------------------------------------------------
// Einstellungen: Stufen, Basiszins, Berechtigungen
// ---------------------------------------------------------------------------

export async function listMahnstufen(): Promise<Mahnstufe[]> {
  const data = await api.get<{ items: Mahnstufe[] }>("/mahnwesen/stufen");
  return data.items || [];
}

export async function createMahnstufe(stufe: Partial<Mahnstufe>): Promise<Mahnstufe> {
  return api.post<Mahnstufe>("/mahnwesen/stufen", stufe);
}

export async function updateMahnstufe(id: number, stufe: Partial<Mahnstufe>): Promise<Mahnstufe> {
  return api.put<Mahnstufe>(`/mahnwesen/stufen/${id}`, stufe);
}

export async function deleteMahnstufe(id: number): Promise<{ ok: boolean; deaktiviert?: boolean }> {
  return api.del(`/mahnwesen/stufen/${id}`);
}

/** Mitgelieferte Standardtexte für eine Stufe (zum Zurücksetzen im Stufen-Dialog). */
export async function getMahnstufeStandardtexte(
  code: string,
  sortierung?: number | null,
): Promise<MahnstufeStandardtexte> {
  const qs = new URLSearchParams({ code });
  if (sortierung != null) qs.set("sortierung", String(sortierung));
  return api.get<MahnstufeStandardtexte>(`/mahnwesen/stufen/standardtexte?${qs}`);
}

export async function sortiereMahnstufen(ids: number[]): Promise<Mahnstufe[]> {
  const data = await api.post<{ items: Mahnstufe[] }>("/mahnwesen/stufen/sortierung", { ids });
  return data.items || [];
}

export async function getMahnwesenEinstellungen(): Promise<MahnwesenEinstellungen> {
  return api.get<MahnwesenEinstellungen>("/mahnwesen/einstellungen");
}

export async function saveMahnwesenEinstellungen(
  einstellungen: MahnwesenEinstellungen,
): Promise<MahnwesenEinstellungen> {
  return api.put<MahnwesenEinstellungen>("/mahnwesen/einstellungen", einstellungen);
}

export async function getMahnwesenBerechtigungen(): Promise<Record<string, string[]>> {
  return api.get<Record<string, string[]>>("/mahnwesen/berechtigungen");
}

export async function saveMahnwesenBerechtigungen(
  berechtigungen: Record<string, string[]>,
): Promise<Record<string, string[]>> {
  return api.put<Record<string, string[]>>("/mahnwesen/berechtigungen", berechtigungen);
}

export async function listBasiszinssaetze(): Promise<{
  items: BasiszinsSatz[];
  stand: BasiszinsStand;
}> {
  return api.get("/mahnwesen/basiszinssaetze");
}

export async function saveBasiszinssatz(satz: {
  gueltig_ab: string;
  zinssatz_prozent: number;
  quelle?: string;
  notiz?: string;
}): Promise<BasiszinsSatz> {
  return api.post<BasiszinsSatz>("/mahnwesen/basiszinssaetze", satz);
}

export async function deleteBasiszinssatz(id: number): Promise<{ ok: boolean }> {
  return api.del(`/mahnwesen/basiszinssaetze/${id}`);
}

// ---------------------------------------------------------------------------
// Sperren + Vereinbarungen
// ---------------------------------------------------------------------------

export async function listMahnsperren(params?: {
  dokument_id?: number;
  kunden_id?: number;
  nur_aktive?: boolean;
}): Promise<Mahnsperre[]> {
  const qs = new URLSearchParams();
  if (params?.dokument_id != null) qs.set("dokument_id", String(params.dokument_id));
  if (params?.kunden_id != null) qs.set("kunden_id", String(params.kunden_id));
  if (params?.nur_aktive === false) qs.set("nur_aktive", "false");
  const suffix = qs.toString() ? `?${qs}` : "";
  const data = await api.get<{ items: Mahnsperre[] }>(`/mahnwesen/sperren${suffix}`);
  return data.items || [];
}

export async function setzeMahnsperre(sperre: {
  dokument_id?: number;
  kunden_id?: number;
  status?: string;
  grund?: string;
  gueltig_bis?: string;
}): Promise<Mahnsperre> {
  return api.post<Mahnsperre>("/mahnwesen/sperren", sperre);
}

export async function beendeMahnsperre(id: number): Promise<{ ok: boolean }> {
  return api.post(`/mahnwesen/sperren/${id}/beenden`, {});
}

// ---------------------------------------------------------------------------
// Rechnungsbezogen + Kundenkonto
// ---------------------------------------------------------------------------

export async function getDokumentMahnung(docId: number): Promise<DokumentMahnungStatus> {
  return api.get<DokumentMahnungStatus>(`/dokumente/${docId}/mahnung`);
}

export async function setMahnungPause(
  docId: number,
  pausiert: boolean,
): Promise<{ ok: boolean; mahnung_pausiert: boolean }> {
  return api.post(`/dokumente/${docId}/mahnung/pause`, { pausiert });
}

export async function setzeZahlungsfrist(
  docId: number,
  body: { faelligkeitsdatum: string; pause_aufheben?: boolean },
): Promise<{
  ok: boolean;
  faelligkeitsdatum: string;
  verzugsbeginn: string | null;
  mahnung_pausiert: boolean;
}> {
  return api.post(`/dokumente/${docId}/mahnung/frist`, body);
}

/** Lokales Datum plus Tage, ohne UTC-Verschiebung um Mitternacht. */
export function isoDatumInTagen(tage: number, ab: Date = new Date()): string {
  const d = new Date(ab.getFullYear(), ab.getMonth(), ab.getDate() + tage);
  const monat = String(d.getMonth() + 1).padStart(2, "0");
  const tag = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${monat}-${tag}`;
}

export async function getKundenKonto(kundenId: number): Promise<KundenKonto> {
  return api.get<KundenKonto>(`/mahnwesen/kunden/${kundenId}`);
}

/**
 * Kundenkonto über die Geschäftspartner-ID.
 * `kunden_id` ist null, wenn dem GP kein Abrechnungs-Kunde zugeordnet ist.
 */
export async function getKundenKontoByGp(
  gpId: number,
): Promise<(KundenKonto & { kunden_id: number }) | { kunden_id: null }> {
  return api.get(`/mahnwesen/kunden/by-gp/${gpId}`);
}

export type KundenRechnungZahlungsstatus = "offen" | "teilbezahlt" | "bezahlt";

export interface KundenRechnung {
  id: string;
  dokument_id: number | null;
  hero_document_id: number | null;
  dokument_nummer: string | null;
  belegdatum: string | null;
  faelligkeitsdatum: string | null;
  rechnung_art: string | null;
  betreff: string | null;
  auftragsnummer: string | null;
  summe_brutto: number;
  summe_offen: number;
  zahlungsstatus: KundenRechnungZahlungsstatus;
  objektadresse: string | null;
}

export interface KundenRechnungen {
  kundennummer: string | null;
  truncated: boolean;
  rechnungen: KundenRechnung[];
}

export async function getKundenRechnungenByGp(gpId: number): Promise<KundenRechnungen> {
  return api.get<KundenRechnungen>(`/mahnwesen/kunden/by-gp/${gpId}/rechnungen`);
}

// ---------------------------------------------------------------------------
// Format-Helfer
// ---------------------------------------------------------------------------

export function formatMahnungDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("de-DE");
}

/** Kompatibilität: Legacy-Stufencodes → Label (Offene-Posten-Seite). */
export const MAHNSTUFE_LABELS: Record<string, string> = {
  keine: "Keine",
  erinnerung: "Zahlungserinnerung",
  mahnung_1: "1. Mahnung",
  mahnung_2: "2. Mahnung",
  mahnung_3: "3. Mahnung",
};
